<template>
	<div class="page-register">
		<div class="container">
			<!-- Login Logo -->
			<div class="login-logo text-center" v-if="!isCreated">
				<picture>
					<img src="/img/logo/grafias_medium_ondark.png" class="d-inline-block m-auto" loading="lazy" alt="Grafias">
				</picture>
			</div>
			<!-- Login Box -->
			<div class="login-box register-box" v-if="!isCreated">
				<div class="login-box-content">
					<div class="login-error callout callout--danger hidden" v-if="loginError">
						<div class="callout__content">{{ loginError }}</div>
					</div>
					<form @submit.prevent="checkDisposibleEmail">
						<div class="mb-3">
							<h2 class="font-weight-normal">{{ $t('Sign Up') }}</h2>
						</div>
						<!-- Email -->
						<div class="form-group">
							<label>{{ $t('Email') }}</label>
							<input type="email" name="email" class="form-control" v-model="email" required>
							<span v-if="!isValidEmailAddress && email !== ''" class="text-danger text-small">{{$t('Invalid Email Address')}}</span>
						</div>
						<!-- Fullname -->
						<div class="form-group">
							<label>{{ $t('Fullname') }}</label>
							<input type="text" name="name" class="form-control" v-model="fullname" required @keydown="textKeydown($event)">
						</div>
						<!-- Password -->
						<div class="form-group mb-4">
							<div class="d-flex justify-content-between">
								<label>{{ $t('Password') }}</label>
								<span class="font-small mt-1"
									:class="{'very-weak': warningError === 'Weak',
										'weak': warningError === 'Medium',
										'strong': warningError === 'Strong',
									}">
									{{warningError}}
								</span>
							</div>
							<input-password v-model="password" :required="false"/>
							<span class="text-danger text-small" v-if="warningError === 'Weak' && password">{{$t('Please enter a password of at least 8 characters, and use a combination of letters and numbers')}}</span>
						</div>
						<!-- Confirm Password -->
						<div class="form-group mb-4">
						<div class="d-flex justify-content-between">
							<label>{{ $t('Confirm Password') }}</label>
							<label class="text-danger text-small" v-if="confirmPassword && confirmPassword !== password">{{$t('Password do not match')}}</label>
						</div>
							<input-password v-model="confirmPassword" />
						</div>
						<!-- Promo Code -->
						<div class="form-group mb-4 relative">
							<label>{{ $t('Promo Code') }}</label>
							<input type="text" name="promo-code" class="form-control" v-model="promoCode" >
							<div class="loading-promo-default">
								<div v-if="isChecking" class="is-loading"></div>
							</div>
						</div>
						<label class="custom-control custom-checkbox mb-3">
							<input type="checkbox" class="custom-control-input" v-model="isAgree">
							<span class="custom-control-label">{{ $t('I agree with Grafias') }} <a target="_blank" href="/tos" class="text-white td-u">terms of service</a></span>
						</label>
						<label class="custom-control custom-checkbox mb-3">
							<input type="checkbox" class="custom-control-input" v-model="isReceiveUpdates">
							<span class="custom-control-label">{{ $t('I`d like to receive product updates and promotions') }}</span>
						</label>
						<!-- Submit -->
							<button
								type="submit"
								class="btn btn-submit btn-primary mt-3"
								:class="{ 'is-loading': isCreating }"
								:disabled="!isFormValid || !isAgree">
								{{ $t('Create Account') }}
							</button>
						<div class="login-extra d-flex text-left mt-3">
							<div class="pt-2"><label class="mr-2">{{$t('Already have an account? ')}}</label>
							<router-link class="login-action mr-2" to="/login">{{ $t('Sign in') }}</router-link></div>
							<!-- <div><router-link class="login-action" to="/help">{{ $t('Help') }}</router-link></div> -->
						</div>
							<a href="https://grafi.as/terms-and-conditions/" target="_blank" class="login-action">{{ $t('Terms & Conditions') }}</a>
					</form>
				</div>
			</div>
			<div class="message-box text-left" v-else>
				<div class="login-logo">
					<picture>
						<img src="/img/logo/grafias_medium_ondark.png" class="d-inline-block m-auto" loading="lazy" alt="Grafias">
					</picture>
				</div>
				<div class="pl-1">
					<h3 class="mb-4">{{ $t('Follow this simple steps to continue registration') }}:</h3>
					<div class="text-lg mb-1">{{ $t('We have sent you an verification link to') }}: {{ email }}</div>
					<div class="text-lg mb-1">{{ $t('It can take a few minutes to arrive in your inbox') }}.</div>
					<div class="text-lg mb-4">{{ $t('Click the confirmation link to log in') }}.</div>
					<div class="text-lg mb-4">{{ $t('No email? Check spam folder, or click')}} <router-link class="text-primary" to="/confirmation-email">{{ $t('here') }}</router-link> {{$t('to resend')}}</div>
					<div class="text-left">
						<router-link
							to="/login"
							class="btn btn-submit btn-primary mt-3">
						{{ $t('Back To Login') }}
						</router-link>
					</div>
				</div>
			</div>
		</div>
		<div class="modals">
			<!-- Error -->
			<modal class="login-error-modal" size="small" v-if="error" @close="error = false">
				<div slot="header">{{$t('Login Error')}}</div>
				<div slot="body" class="text-left">
					<div v-if="errorMessage === ''">{{ $t('Wrong Email or Password') }}</div>
					<div v-else>{{ errorMessage }}</div>
				</div>
				<div slot="footer">
					<button type="button" class="btn btn-secondary" @click="error = false">OK</button>
				</div>
			</modal>
		</div>
		<footer
			class="footer footer-main">
			<div class="d-flex justify-content-between">
				<div>
					<div class="footer-brand text-grey"><span>{{ $t('Ver.') }} {{ appVersion }}</span></div>
				</div>
			</div>
		</footer>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Modal from '@/components/Modal.vue';
import InputPassword from '@/components/InputPassword.vue';
import { getAxiosErrorMessage, isValidEmail, disposableEmail } from '@/lib/helper';
import auth from '@/api/auth';
import promoCodesApi from '@/api/promoCode';
import {
	appVersion,
} from '@/lib/statics';

export default {
	name: 'Register',
	components: {
		Modal,
		InputPassword,
	},
	data() {
		return {
			email: '',
			password: '',
			typePassword: 'password',
			typeConfirmPassword: 'password',
			fullname: '',
			phone: '',
			confirmPassword: '',
			lang: 'en',
			isShowResetPassword: false,
			isResettingPassword: false,
			resetPasswordError: false,
			resetPasswordSuccess: false,
			error: false,
			errorMessage: '',
			isAgree: true,
			isReceiveUpdates: true,
			isCreating: false,
			isCreated: false,
			warningError: '',
			score: '',
			isDisposableEmail: '',
			isChecking: false,
			promoCode: '',
		};
	},
	methods: {
		...mapActions({
			setLocale: 'setLocale',
		}),
		create() {
			this.isCreating = true;
			const email = this.email.trim();
			const fullname = this.fullname.trim();
			const phone = this.phone.trim();
			const params = {
				email,
				password: this.password,
				name: fullname,
				phone,
				is_receive_updates: this.isReceiveUpdates,
				verification_method: 'email',
				lang: this.lang,
			};
			if (this.promoCode) params.promo_code = this.promoCode;
			const callback = () => {
				this.isCreated = true;
				this.isCreating = false;
			};
			const errorCallback = (e) => {
				const errorMesssages = getAxiosErrorMessage(e);
				const message = errorMesssages;
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Sign Up',
					text: message,
					duration: 6000,
				});
				this.isCreating = false;
			};
			auth.register(params, callback, errorCallback);
		},
		showPassword() {
			if (this.typePassword === 'password') {
				this.typePassword = 'text';
			} else {
				this.typePassword = 'password';
			}
		},
		showConfirmPassword() {
			if (this.typeConfirmPassword === 'password') {
				this.typeConfirmPassword = 'text';
			} else {
				this.typeConfirmPassword = 'password';
			}
		},
		textKeydown(e) {
			if (/[^A-Z0-9 ]/gi.test(e.key)) {
				e.preventDefault();
			}
		},
		checkDisposibleEmail() {
			this.isCreating = true;
			const callback = (response) => {
				const debounce = response.data.debounce.reason;
				if (debounce === 'Disposable') {
					this.$notify({
						group: 'app',
						type: 'error',
						title: 'Sign Up',
						text: 'Please do not use disposable e-mails',
						duration: 9000,
					});
				} else {
					this.create();
				}
				this.isCreating = false;
			};
			const errorCallback = () => {
				this.handleErrors();
			};
			auth.checkDisposableEmail(this.email, callback, errorCallback);
		},
		handleErrors() {
			const splitEmail = this.email.split('@')[1];
			this.isDisposableEmail = disposableEmail(splitEmail);
			if (this.isDisposableEmail) {
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Sign Up',
					text: 'Please do not use disposable e-mails',
					duration: 9000,
				});
			} else {
				this.create();
			}
		},
		checkPromoDefault() {
			this.isChecking = true;
			const callback = (response) => {
				if (this.promoCodeParam) {
					this.promoCode = this.promoCodeParam;
				} else {
					this.promoCode = response && response.data && response.data.code ? response.data.code : '';
				}
				if (this.promoCode) {
					this.$notify({
						group: 'app',
						type: 'success',
						title: 'Sign Up',
						text: 'Promo Applied',
						duration: 9000,
					});
				}
				this.isChecking = false;
			};
			const errorCallback = () => {
				this.isChecking = false;
				if (this.promoCodeParam) this.promoCode = this.promoCodeParam;
			};
			promoCodesApi.getDefaultPromo(callback, errorCallback);
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			locale: 'locale',
			loginError: 'loginError',
		}),
		action() {
			const { action } = this.$route.query;
			return action;
		},
		isFormValid() {
			return (
				this.password === this.confirmPassword
				&& this.warningError !== 'Weak'
				&& this.isValidEmailAddress
				&& this.fullname !== ''
			);
		},
		appVersion() {
			return appVersion;
		},
		isValidEmailAddress() {
			return isValidEmail(this.email);
		},
		promoCodeParam() {
			return this.$route.query.promo_code;
		},
	},
	watch: {
		password() {
			if (this.password !== '') {
				/* eslint-disable global-require */
				const passwordStrength = require('check-password-strength');
				if (this.password.length < 8) {
					this.warningError = 'Weak';
				} else {
					/* eslint-enable global-require */
					this.warningError = passwordStrength(this.password).value;
				}
			}
			if (this.password === '') this.warningError = '';
		},
		loginError(loginError) {
			if (loginError) {
				this.error = true;
				this.errorMessage = loginError;
			}
		},
	},
	created() {
		this.checkPromoDefault();
		document.body.classList.add('login');
		if (this.action && this.action === 'forgot_password') {
			this.isShowResetPassword = true;
		}
	},
	destroyed() {
		document.body.classList.remove('login');
	},
};
</script>
